/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useWindowSize } from '../hooks/useWindowSize';
import { useData } from '../backend/firebase';

// --- 0. SETTINGS HOOK ---
const usePersistentState = (key, initialValue) => {
    const [state, setState] = useState(() => {
        try {
            const item = window.localStorage.getItem(key);
            return item ? JSON.parse(item) : initialValue;
        } catch (error) { return initialValue; }
    });
    useEffect(() => { window.localStorage.setItem(key, JSON.stringify(state)); }, [key, state]);
    return [state, setState];
};

const DDS_WASM_VERSION = "1.0.2";

// --- 1. CORE BRIDGE LOGIC & CONSTANTS ---
const SUITS = ['S', 'H', 'D', 'C'];
const RANKS = ['A', 'K', 'Q', 'J', 'T', '9', '8', '7', '6', '5', '4', '3', '2'];
const SEATS = ['W', 'N', 'E', 'S']; 

const SUIT_SYMBOLS = { 'S': '♠', 'H': '♥', 'D': '♦', 'C': '♣' };
const SUIT_COLORS = { 'S': '#111', 'H': '#E53E3E', 'D': '#E53E3E', 'C': '#111' };

const getRankValue = (rank) => {
    if (rank === 'A') return 14; if (rank === 'K') return 13;
    if (rank === 'Q') return 12; if (rank === 'J') return 11;
    if (rank === 'T' || rank === '10') return 10;
    return parseInt(rank);
};

const getVulnerability = (boardNum) => {
    if (!boardNum) return { NS: false, EW: false, label: 'None' };
    const vuls = [
        { NS: false, EW: false, label: 'None' }, { NS: true,  EW: false, label: 'N/S' },
        { NS: false, EW: true,  label: 'E/W' },  { NS: true,  EW: true,  label: 'Both' },
        { NS: true,  EW: false, label: 'N/S' },  { NS: false, EW: true,  label: 'E/W' },
        { NS: true,  EW: true,  label: 'Both' }, { NS: false, EW: false, label: 'None' },
        { NS: false, EW: true,  label: 'E/W' },  { NS: true,  EW: true,  label: 'Both' },
        { NS: false, EW: false, label: 'None' }, { NS: true,  EW: false, label: 'N/S' },
        { NS: true,  EW: true,  label: 'Both' }, { NS: false, EW: false, label: 'None' },
        { NS: true,  EW: false, label: 'N/S' },  { NS: false, EW: true,  label: 'E/W' }
    ];
    return vuls[(boardNum - 1) % 16];
};

const parseContractStr = (str) => {
    if (!str || str.trim() === '' || str.toUpperCase() === 'P' || str.toUpperCase() === 'PASS') {
        return { isPass: true, contract: 'Pass', declarer: '', result: '', trump: 'N' };
    }
    const match = str.trim().match(/^([1-7])([CDHSN])([NESW])(x|xx|)([-=+]?)(\d*)$/i);
    if (!match) return { isPass: true, contract: str, declarer: '', result: '', trump: 'N' };
    return {
        isPass: false, level: match[1], suit: match[2].toUpperCase(),
        declarer: match[3].toUpperCase(), dbl: match[4].toLowerCase(),
        res: match[5], tricks: match[6] || '', trump: match[2].toUpperCase()
    };
};

const getOptimalContracts = (table) => {
    if (!table) return "--";
    const strains = ['NT', 'S', 'H', 'D', 'C'];
    const formatStr = (side1, side2) => {
        const makes = strains.map(s => {
            const tricks = Math.max(table[`${side1}_${s}`] || 0, table[`${side2}_${s}`] || 0);
            return tricks > 6 ? `${tricks - 6}<span style="color:${(s === 'H' || s === 'D') ? '#FF3366' : (s === 'NT' || s === 'N') ? '#fff' : '#fff'}">${SUIT_SYMBOLS[s] || 'NT'}</span>` : null;
        }).filter(Boolean);
        return makes.length > 0 ? makes.join(' ') : 'Pass';
    };
    
    // Using dangerouslySetInnerHTML allows us to render the red suits correctly
    return (
        <span style={{ display: 'flex', gap: 10 }}>
            <span>NS: <span dangerouslySetInnerHTML={{ __html: formatStr('N', 'S') }} /></span>
            <span style={{color: 'rgba(255,255,255,0.2)'}}>|</span>
            <span>EW: <span dangerouslySetInnerHTML={{ __html: formatStr('E', 'W') }} /></span>
        </span>
    );
};

const getParScore = (table, vulData) => {
    if (!table) return null;
    
    // Standard duplicate bridge scoring calculator
    const getScore = (level, suit, tricks, vul) => {
        const target = level + 6;
        if (tricks < target) {
            const down = target - tricks;
            // Sacrifices in a par algorithm are ALWAYS assumed to be doubled
            if (vul) return -(200 + (down - 1) * 300);
            if (down === 1) return -100;
            if (down === 2) return -300;
            if (down === 3) return -500;
            return -(500 + (down - 3) * 300);
        }
        let trickPts = (suit === 'C' || suit === 'D') ? level * 20 : level * 30;
        if (suit === 'NT') trickPts = 40 + (level - 1) * 30;
        let overtricks = (tricks - target) * ((suit === 'C' || suit === 'D') ? 20 : 30);
        let gameBonus = trickPts >= 100 ? (vul ? 500 : 300) : 50;
        let slamBonus = level === 6 ? (vul ? 750 : 500) : (level === 7 ? (vul ? 1500 : 1000) : 0);
        return trickPts + gameBonus + overtricks + slamBonus;
    };

    // Strains strictly ordered by bidding hierarchy
    const strains = ['C', 'D', 'H', 'S', 'NT'];
    const bids = [];
    for (let l = 1; l <= 7; l++) {
        for (let s of strains) {
            const nsT = Math.max(table[`N_${s}`] ?? 0, table[`S_${s}`] ?? 0);
            const ewT = Math.max(table[`E_${s}`] ?? 0, table[`W_${s}`] ?? 0);
            bids.push({
                level: l, suit: s, nsTricks: nsT, ewTricks: ewT,
                nsScore: getScore(l, s, nsT, vulData.NS),
                ewScore: getScore(l, s, ewT, vulData.EW)
            });
        }
    }

    // 1. Find the highest making scores to start the bidding war
    let nsBestMake = bids.filter(b => b.nsTricks >= b.level + 6).reduce((max, b) => b.nsScore > max.nsScore ? b : max, {nsScore: -9999, idx: -1});
    let ewBestMake = bids.filter(b => b.ewTricks >= b.level + 6).reduce((max, b) => b.ewScore > max.ewScore ? b : max, {ewScore: -9999, idx: -1});
    
    if (nsBestMake.nsScore === -9999 && ewBestMake.ewScore === -9999) return <span style={{color: '#fff', fontWeight: 'bold'}}>Pass</span>;

    let leadingSide = nsBestMake.nsScore > ewBestMake.ewScore ? 'NS' : 'EW';
    let bestMake = leadingSide === 'NS' ? nsBestMake : ewBestMake;
    
    let bestIdx = bids.findIndex(b => b.level === bestMake.level && b.suit === bestMake.suit);
    let parBid = bestMake;
    let parScore = leadingSide === 'NS' ? bestMake.nsScore : bestMake.ewScore;
    let isDoubled = false;

    // 2. Sweep all higher bids to see if the opponent should sacrifice
    for (let i = bestIdx + 1; i < bids.length; i++) {
        const sacBid = bids[i];
        const oppSide = leadingSide === 'NS' ? 'EW' : 'NS';
        const sacScore = oppSide === 'EW' ? sacBid.ewScore : sacBid.nsScore;
        
        // If the penalty for sacrificing is better than defending the current contract
        if (sacScore > -parScore) {
            parBid = sacBid;
            parScore = sacScore;
            leadingSide = oppSide;
            isDoubled = parBid[`${leadingSide === 'NS' ? 'ns' : 'ew'}Tricks`] < parBid.level + 6;
        }
    }

    const color = (parBid.suit === 'H' || parBid.suit === 'D') ? '#FF3366' : '#fff';
    const tricks = leadingSide === 'NS' ? parBid.nsTricks : parBid.ewTricks;
    const diff = tricks - (parBid.level + 6);
    const resStr = diff === 0 ? '=' : (diff > 0 ? `+${diff}` : `${diff}`);

    return (
        <span style={{ display: 'flex', gap: 6, alignItems: 'center', background: 'rgba(0,0,0,0.4)', padding: '4px 10px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.1)' }}>
            <span style={{color: '#fff', fontWeight: '900'}}>{leadingSide}</span>
            <span style={{color: '#fff', fontWeight: 'bold'}}>{parBid.level}<span style={{color}}>{SUIT_SYMBOLS[parBid.suit] || 'NT'}</span>{isDoubled ? 'x' : ''} {resStr}</span>
            <span style={{color: 'rgba(255,255,255,0.2)'}}>|</span>
            <span style={{color: parScore > 0 ? '#4ADE80' : '#FF8A8A', fontWeight: 'bold'}}>{parScore > 0 ? `+${parScore}` : parScore}</span>
        </span>
    );
};

const formatContract = (contractStr) => {
    if (!contractStr || contractStr.trim() === '' || contractStr.toUpperCase() === 'P' || contractStr.toUpperCase() === 'PASS') {
        return <span style={{color: '#8F9EB5'}}>Pass</span>;
    }
    
    const match = contractStr.trim().match(/^([1-7])([CDHSN])(.*)$/i);
    if (!match) return <span>{contractStr}</span>;
    
    const level = match[1];
    const suit = match[2].toUpperCase();
    const rest = match[3];
    
    const SYMBOLS = { 'S': '♠', 'H': '♥', 'D': '♦', 'C': '♣', 'N': 'NT' };
    const COLORS = { 'S': '#fff', 'H': '#FF3366', 'D': '#FF3366', 'C': '#fff', 'N': '#fff' };
    const WIN_COLOR = '#6cbd8c'; 
    const LOSE_COLOR = '#ed7666';
    
    let modifiers = rest;
    let resultSpan = null; 

    const resultMatch = rest.match(/^(.*?)([-=+](?:\d+)?)$/);
    if (resultMatch) {
        modifiers = resultMatch[1]; 
        const resultText = resultMatch[2]; 
        const isWin = resultText.startsWith('=') || resultText.startsWith('+');
        resultSpan = <span style={{color: '#fff', marginLeft: 6}}>{resultText}</span>;
    }
    
    return (
        <span style={{display: 'inline-flex', alignItems: 'center'}}>
            <span style={{color:'#fff'}}>
                {level}<span style={{color: COLORS[suit]}}>{SYMBOLS[suit]}</span>{modifiers}
            </span>
            {resultSpan}
        </span>
    );
};


const reconstructHands = (handsStr) => {
    if (!handsStr) return { S: [], W: [], N: [], E: [], dealer: 'S' };
    const dealerMap = { '1': 'S', '2': 'W', '3': 'N', '4': 'E' };
    const dealer = dealerMap[handsStr.charAt(0)] || 'S';
    const parts = handsStr.substring(1).split(',');
    
    let parsedHands = { 0: parts[0] || '', 1: parts[1] || '', 2: parts[2] || '', 3: parts[3] || '' };

    // Safely translates a LIN string chunk into an array of card objects
    const toArr = (s) => {
        let arr = []; let cur = '';
        for (let i = 0; i < s.length; i++) {
            if (SUITS.includes(s[i])) cur = s[i];
            else if (cur) {
                let r = s[i];
                // Safely convert '10' or '1' to 'T' without skipping next characters if natively 'T'
                if (r === '1' && s[i+1] === '0') { r = 'T'; i++; }
                else if (r === '1') r = 'T';
                
                if (RANKS.includes(r)) {
                    arr.push({ suit: cur, rank: r, id: `${cur}${r}` });
                }
            }
        }
        return arr;
    };

    let hands = { 
        S: toArr(parsedHands[0]), 
        W: toArr(parsedHands[1]), 
        N: toArr(parsedHands[2]), 
        E: toArr(parsedHands[3]) 
    };

    // Find the missing hand and populate it accurately
    let missingIdx = -1;
    Object.keys(parsedHands).forEach(k => { if (parsedHands[k] === '') missingIdx = parseInt(k); });
    
    if (missingIdx !== -1) {
        let deck = {}; SUITS.forEach(s => deck[s] = [...RANKS]);
        
        // Remove strictly known cards
        Object.values(hands).forEach(handArr => {
            handArr.forEach(card => {
                deck[card.suit] = deck[card.suit].filter(r => r !== card.rank);
            });
        });

        // Deal remainder to the missing hand
        let missingHandArr = [];
        SUITS.forEach(s => {
            deck[s].forEach(r => missingHandArr.push({ suit: s, rank: r, id: `${s}${r}` }));
        });
        
        const idxMap = {0: 'S', 1: 'W', 2: 'N', 3: 'E'};
        hands[idxMap[missingIdx]] = missingHandArr;
    }

    return { ...hands, dealer };
};

// --- DOUBLE DUMMY HELPERS ---
const getTrumpInt = (suit) => {
    const map = { 'S': 0, 'H': 1, 'D': 2, 'C': 3, 'N': 4 };
    return map[suit] !== undefined ? map[suit] : 4;
};

const getSeatInt = (seat) => {
    const map = { 'N': 0, 'E': 1, 'S': 2, 'W': 3 };
    return map[seat] !== undefined ? map[seat] : 0;
};

const convertToPBN = (hands, currentTrick = []) => {
    // Extract just the IDs of the cards currently on the table
    const trickIds = currentTrick.length === 4 ? [] : currentTrick.map(t => t.card.id);
    // const trickIds = currentTrick.map(t => t.card.id);

    const formatHand = (handArray) => {
        // Keep cards that are completely unplayed, OR are currently in the active trick
        const validCards = handArray.filter(c => !c.isPlayed || trickIds.includes(c.id));
        const getSuit = (suit) => validCards.filter(c => c.suit === suit).map(c => c.rank === '10' ? 'T' : c.rank).join('');
        return `${getSuit('S')}.${getSuit('H')}.${getSuit('D')}.${getSuit('C')}`;
    };

    const maxCards = Math.max(
        hands.N.filter(c => !c.isPlayed || trickIds.includes(c.id)).length,
        hands.E.filter(c => !c.isPlayed || trickIds.includes(c.id)).length,
        hands.S.filter(c => !c.isPlayed || trickIds.includes(c.id)).length,
        hands.W.filter(c => !c.isPlayed || trickIds.includes(c.id)).length
    );

    if (maxCards > 13) {
        console.error(`🚨 ABORTING DDS: Invalid state. A player has ${maxCards} cards.`);
        return null; 
    }

    return `N:${formatHand(hands.N)} ${formatHand(hands.E)} ${formatHand(hands.S)} ${formatHand(hands.W)}`;
};


const PlayingCard = ({ card, hidden, isPlayed, ddScore, onClick, onDoubleClick, alignRight }) => {
    if (!card) return <div style={{width: 32, height: 48, borderRadius: 4, border: '1px dashed rgba(255,255,255,0.1)'}} />;

    let badge = null;
    if (ddScore !== undefined && ddScore !== null) {
        const isSuccess = ddScore >= 0;
        const text = ddScore === 0 ? '=' : (ddScore > 0 ? `+${ddScore}` : `${ddScore}`);

        badge = (
            <div style={{
                position: 'absolute', bottom: 10, left: alignRight ? 'auto' : 0, right: alignRight ? 0 : 'auto',
                background: isSuccess ? '#26A65B' : '#E53E3E', color: '#fff', fontSize: 9, fontWeight: '900',
                padding: '1px 4px', borderRadius: 4, boxShadow: '0 2px 4px rgba(0,0,0,0.8)', pointerEvents: 'none',
                whiteSpace: 'nowrap', zIndex: 9999
            }}>
                {text}
            </div>
        );
    }

    if (hidden) {
        return (
            <div 
                onClick={onClick} onDoubleClick={onDoubleClick}
                style={{
                    position: 'relative', width: 32, height: 48, borderRadius: 4, 
                    background: 'linear-gradient(135deg, #2D2B3F 25%, #1A1C29 100%)', 
                    border: '1px solid rgba(255,255,255,0.2)', cursor: onClick ? 'pointer' : 'default',
                    boxShadow: '-1px 1px 3px rgba(0,0,0,0.4)', zIndex: badge ? 10 : 1
                }}
            >
                {badge} {/* Badge renders OVER the card back! */}
            </div>
        );
    }

    return (
        <div 
            onClick={onClick} onDoubleClick={onDoubleClick}
            style={{ 
                position: 'relative', width: 32, height: 48, background: '#F0F2F5', 
                border: '0.5px solid rgba(78, 78, 78, 0.2)', borderRadius: 4, 
                cursor: onClick ? 'pointer' : 'default', opacity: isPlayed ? 0.3 : 1,
                boxShadow: '-1px 1px 3px rgba(0,0,0,0.4)', zIndex: badge ? 10 : 1 
            }}
        >
            {badge}
            <div style={{ position: 'absolute', top: 2, left: alignRight ? 'auto' : 3, right: alignRight ? 3 : 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1 }}>
                <div style={{ color: SUIT_COLORS[card.suit], fontSize: 13, fontWeight: 'bold' }}>{card.rank}</div>
                <div style={{ color: SUIT_COLORS[card.suit], fontSize: 14, marginTop: -2 }}>{SUIT_SYMBOLS[card.suit]}</div>
            </div>
        </div>
    );
};

// // --- 2. VISUAL COMPONENTS ---
// const PlayingCard = ({ card, hidden, isPlayed, ddScore, onClick, alignRight }) => {
//     if (!card) return <div style={{width: 32, height: 48, borderRadius: 4, border: '1px dashed rgba(255,255,255,0.1)'}} />;
//     if (hidden) return <div style={{width: 32, height: 48, borderRadius: 4, background: 'linear-gradient(135deg, #2D2B3F 25%, #1A1C29 100%)', border: '1px solid rgba(255,255,255,0.2)'}} />;

//     let badge = null;
//     if (ddScore !== undefined && ddScore !== null) {
//         const isSuccess = ddScore >= 0;
//         const text = ddScore === 0 ? '=' : (ddScore > 0 ? `+${ddScore}` : `${ddScore}`);


//         badge = (
//             <div style={{
//                 position: 'absolute',
//                 bottom: 10,  // Moved to the bottom
//                 left: alignRight ? 'auto' : 0,    // Anchored to the left
//                 right: alignRight ? 0 : 'auto',
//                 background: isSuccess ? '#26A65B' : '#E53E3E', 
//                 color: '#fff',
//                 fontSize: 9, // Made smaller
//                 fontWeight: '900',
//                 padding: '1px 4px', // Tighter padding
//                 borderRadius: 4,
//                 boxShadow: '0 2px 4px rgba(0,0,0,0.8)',
//                 pointerEvents: 'none',
//                 whiteSpace: 'nowrap',
//                 zIndex: 9999 // High z-index for the badge itself
//             }}>
//                 {text}
//             </div>
//         );
//         // badge = (
//         //     <div style={{
//         //         position: 'absolute',
//         //         top: -10, 
//         //         // Shift badge to the left if text is on the right
//         //         right: alignRight ? 'auto' : -10,
//         //         left: alignRight ? -10 : 'auto',
//         //         background: isSuccess ? '#26A65B' : '#E53E3E', 
//         //         color: '#fff',
//         //         fontSize: 10,
//         //         fontWeight: '900',
//         //         padding: '2px 5px',
//         //         borderRadius: 4,
//         //         boxShadow: '0 3px 6px rgba(0,0,0,0.8)',
//         //         pointerEvents: 'none',
//         //         whiteSpace: 'nowrap',
//         //         zIndex: 999 
//         //     }}>
//         //         {text}
//         //     </div>
//         // );
//     }

//     return (
//         <div 
//             onClick={onClick}
//             style={{ 
//                 position: 'relative', 
//                 width: 32, height: 48, 
//                 background: '#F0F2F5', 
//                 border: '0.5px solid rgba(78, 78, 78, 0.2)', 
//                 borderRadius: 4, 
//                 cursor: onClick ? 'pointer' : 'default',
//                 opacity: isPlayed ? 0.3 : 1,
//                 boxShadow: '-1px 1px 3px rgba(0,0,0,0.4)', 
//                 zIndex: badge ? 10 : 1 
//             }}
//         >
//             {badge}
            
//             {/* Dynamically anchor text to Left or Right based on the prop */}
//             <div style={{ position: 'absolute', top: 2, left: alignRight ? 'auto' : 3, right: alignRight ? 3 : 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1 }}>
//                 <div style={{ color: SUIT_COLORS[card.suit], fontSize: 13, fontWeight: 'bold' }}>{card.rank}</div>
//                 <div style={{ color: SUIT_COLORS[card.suit], fontSize: 14, marginTop: -2 }}>{SUIT_SYMBOLS[card.suit]}</div>
//             </div>
//         </div>
//     );
// };


// const PlayerBadge = ({ seat, name, realName, isVul, teamColor }) => {
//     // Opacity bumped to 0.95 so it acts as a solid plate over the cards
//     const bgColor = isVul ? 'rgba(169, 68, 68, 0.95)' : 'rgba(45, 90, 60, 0.95)';
    
//     return (
//         <div style={{ 
//             display: 'flex', alignItems: 'center', gap: 4, 
//             background: bgColor, 
//             padding: '3px 8px', // Smaller padding
//             borderRadius: 6, 
//             border: `1px solid ${ 'rgba(188, 188, 188, 0.74)'}`,
//             boxShadow: `0 4px 12px rgba(0,0,0,0.8)`,
//             backdropFilter: 'blur(4px)' // Ensures text readability over cards
//         }}>
//             <div style={{ fontWeight: '900', color: '#fff', fontSize: 10 }}>{seat}</div>
//             <div style={{ fontSize: 10, color: '#E0E0E0', maxWidth: 60, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'help' }} title={realName || name}>
//                 {name || 'Unknown'}
//             </div>
//         </div>
//     );
// };

const PlayerBadge = ({ seat, name, realName, isVul, teamColor, onClick }) => {
    const bgColor = isVul ? 'rgba(169, 68, 68, 0.95)' : 'rgba(45, 90, 60, 0.95)';
    return (
        <div onClick={onClick} style={{ 
            display: 'flex', alignItems: 'center', gap: 6, background: bgColor, 
            padding: '4px 10px', borderRadius: 8, border: `1px solid ${teamColor || 'rgba(188, 188, 188, 0.74)'}`,
            boxShadow: `0 4px 10px rgba(0,0,0,0.3)`, cursor: onClick ? 'pointer' : 'default'
        }}>
            <div style={{ fontSize: 11, color: '#E0E0E0', maxWidth: 70, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={realName || name}>
                {name || 'Unknown'}
            </div>
        </div>
    );
};
// const PlayerBadge = ({ seat, name, realName, isVul, teamColor }) => {
//     // Washed out backgrounds for Vulnerable (Red) and Non-Vulnerable (Green)
//     const bgColor = isVul ? 'rgba(169, 68, 68, 0.95)' : 'rgba(45, 90, 60, 0.95)';
    
//     return (
//         <div style={{ 
//             display: 'flex', alignItems: 'center', gap: 6, 
//             background: bgColor, 
//             padding: '4px 10px', 
//             borderRadius: 8, // Slightly squared-off corners look better with full backgrounds
//             border: `1px solid ${teamColor || 'rgba(188, 188, 188, 0.74)'}`,//${teamColor || 'rgba(255,255,255,0.2)'}`,
//             boxShadow: `0 4px 10px rgba(0,0,0,0.3)`
//         }}>
//             {/* <div style={{ fontWeight: '900', color: '#fff', fontSize: 11 }}>{seat}</div> */}
//             <div style={{ fontSize: 11, color: '#E0E0E0', maxWidth: 70, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={realName || name}>
//                 {name || 'Unknown'}
//             </div>
//         </div>
//     );
// };

const PlayerHand = ({ seat, pName, realName, isVul, teamColor, cards, ddScores = {}, trump, onCardClick, onBadgeClick, isHiddenRegion, flippedCards = {} }) => {
    const grouped = { S: [], H: [], D: [], C: [] };
    cards.forEach(c => grouped[c.suit].push(c));

    const getSuitOrder = (tSuit) => {
        if (!tSuit || tSuit === 'N' || tSuit === 'S') return ['S', 'H', 'C', 'D'];
        if (tSuit === 'H') return ['H', 'S', 'D', 'C'];
        if (tSuit === 'D') return ['D', 'S', 'H', 'C'];
        if (tSuit === 'C') return ['C', 'H', 'S', 'D'];
        return ['S', 'H', 'C', 'D'];
    };

    const orderedSuits = getSuitOrder(trump);
    const badgeNode = <PlayerBadge seat={seat} name={pName} realName={realName} isVul={isVul} teamColor={teamColor} onClick={() => onBadgeClick && onBadgeClick(seat)} />;

    // --- NORTH & SOUTH HANDS: Single Horizontal Row ---
    if (seat === 'N' || seat === 'S') {
        // Flatten the suits into one continuous line, sorted high to low
        const lineCards = orderedSuits
            .flatMap(suit => [...grouped[suit]].sort((a,b) => getRankValue(b.rank) - getRankValue(a.rank)))
            .filter(c => !c.isPlayed);
        // const lineCards = orderedSuits.flatMap(suit => {
        //     return [...grouped[suit]].sort((a,b) => getRankValue(b.rank) - getRankValue(a.rank));
        // });
        
        return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, width: 'max-content' }}>
                {seat === 'N' && badgeNode}
                <div style={{ display: 'flex', flexDirection: 'row', background: 'rgba(0,0,0,0.0)', padding: '12px', borderRadius: 8 }}>
                    {lineCards.length === 0 && <div style={{width: 32, height: 48}} />}
                    
                    {lineCards.map((c, i) => {
                        const hasScore = ddScores[c.id] !== undefined;
                        const overlap = 16; 
                        
                        return (
                            <div key={c.id} style={{ position: 'relative', marginLeft: i > 0 ? -overlap : 0, zIndex: i }}>
                                <div style={{opacity: c.isPlayed ? 0.2 : 1, transition: '0.3s'}}>
                                    <PlayingCard card={c} hidden={isHiddenRegion && !flippedCards[c.id]} isPlayed={false} ddScore={!c.isPlayed ? ddScores[c.id] : null} onClick={() => onCardClick && onCardClick(c, seat)} />
                                </div>
                            </div>
                        );
                    })}
                </div>
                {seat === 'S' && badgeNode}
            </div>
        );
    }

    // --- EAST & WEST HANDS: 4 Horizontal Rows ---
    const isWest = seat === 'W';
    const alignLayout = isWest ? 'flex-start' : 'flex-end';
    const activeSuits = orderedSuits.filter(suit => grouped[suit].some(c => !c.isPlayed));

    return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: alignLayout, gap: 10, width: 'max-content' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0, padding: '12px', borderRadius: 8, justifyContent: 'center', alignItems: alignLayout }}>
                {activeSuits.length === 0 && <div style={{minHeight: 48}} />}

                {orderedSuits.map((suit, rowIdx) => {
                    // let suitCards = [...grouped[suit]];
                    let suitCards = [...grouped[suit]].filter(c => !c.isPlayed);
                    if (suitCards.length === 0) return <div key={suit} style={{minHeight: 36, minWidth: 32}} />;
                    
                    // SORTING LOGIC: High cards on the "outside"
                    if (isWest) {
                        suitCards.sort((a, b) => getRankValue(b.rank) - getRankValue(a.rank));
                    } else {
                        suitCards.sort((a, b) => getRankValue(a.rank) - getRankValue(b.rank));
                    }
                    
                    const overlap = 18; // suitCards.length > 7 ? 12 : (suitCards.length > 5 ? 14 : 18); 
                    
                    return (
                        <div key={suit} style={{ display: 'flex', flexDirection: 'row', minHeight: 48, minWidth: 32, justifyContent: alignLayout, marginTop: rowIdx > 0 ? -12 : 0 }}>
                            {suitCards.map((c, i) => {
                                const hasScore = ddScores[c.id] !== undefined;
                                
                                // 1. Calculate the base horizontal Z-index
                                let baseZ = i;
                                if (isWest) baseZ = i; 
                                else baseZ = 50 - i; 

                                // 2. Add the vertical boost (lower suits get higher z-indexes)
                                const cascadeZ = baseZ + (rowIdx * 20);

                                return (
                                    <div key={c.id} style={{ position: 'relative', marginLeft: i > 0 ? -(32 - overlap) : 0, zIndex: cascadeZ }}>
                                        <div style={{opacity: c.isPlayed ? 0.2 : 1, transition: '0.3s'}}>
                                            {/* Pass alignRight=true ONLY for West cards */}
                                            <PlayingCard card={c} hidden={isHiddenRegion && !flippedCards[c.id]} isPlayed={false} ddScore={!c.isPlayed ? ddScores[c.id] : null} alignRight={!isWest} onClick={() => onCardClick && onCardClick(c, seat)} />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    );
                })}
            </div>
            {badgeNode}
        </div>
    );
};

const HandDiagram = ({ hands, ddScores }) => {
    const formatSuit = (cards) => {
        if (!cards || cards.length === 0) return '-';
        return cards.map((c) => {
            const rankStr = c.rank === 'T' ? '10' : c.rank;
            const score = ddScores && ddScores[c.id] !== undefined ? ddScores[c.id] : null;
            
            // New legible pills for DD Scores
            let scoreSpan = null;
            if (score !== null && !c.isPlayed) {
                const isSuccess = score >= 0;
                const text = score === 0 ? '=' : (score > 0 ? `+${score}` : `${score}`);
                scoreSpan = (
                    <span style={{ 
                        background: isSuccess ? '#1E4620' : '#4A1515', color: isSuccess ? '#4ADE80' : '#FF8A8A', 
                        padding: '1px 4px', borderRadius: 4, fontSize: 9, marginLeft: 3, verticalAlign: 'middle', fontWeight: 'bold' 
                    }}>
                        {text}
                    </span>
                );
            }
            return (
                <span key={c.id} style={{ opacity: c.isPlayed ? 0.3 : 1, marginRight: 6, display: 'inline-flex', alignItems: 'center' }}>
                    {rankStr}{scoreSpan}
                </span>
            );
        });
    };

    const HandBlock = ({ hand, title }) => (
        <div style={{display: 'flex', flexDirection: 'column', fontSize: 13, lineHeight: 1.4, background: 'rgba(0,0,0,0.0)', padding: 12, borderRadius: 8, border: '1px solid rgba(255,255,255,0.05)', minWidth: 140 }}>
            <div style={{color: '#A0AABF', fontSize: 10, fontWeight: 'bold', marginBottom: 4, textTransform: 'uppercase'}}>{title}</div>
            <div style={{color: '#fff'}}><span style={{color: '#A0AABF'}}>{SUIT_SYMBOLS['S']}</span> {formatSuit(hand.filter(c => c.suit === 'S'))}</div>
            <div style={{color: '#fff'}}><span style={{color: '#FF3366'}}>{SUIT_SYMBOLS['H']}</span> {formatSuit(hand.filter(c => c.suit === 'H'))}</div>
            <div style={{color: '#fff'}}><span style={{color: '#FF3366'}}>{SUIT_SYMBOLS['D']}</span> {formatSuit(hand.filter(c => c.suit === 'D'))}</div>
            <div style={{color: '#fff'}}><span style={{color: '#A0AABF'}}>{SUIT_SYMBOLS['C']}</span> {formatSuit(hand.filter(c => c.suit === 'C'))}</div>
        </div>
    );

    return (
        <div className="ha-table-container">
            <div className="ha-hand-north"><HandBlock hand={hands.N} title="North" /></div>
            <div className="ha-hand-west"><HandBlock hand={hands.W} title="West" /></div>
            <div className="ha-hand-east"><HandBlock hand={hands.E} title="East" /></div>
            <div className="ha-hand-south"><HandBlock hand={hands.S} title="South" /></div>
        </div>
    );
};

const BiddingBox = ({ bidding, dealer, isExpanded, onClick, vulData }) => {
    if (!bidding || bidding.length === 0) return <div style={{color: '#555', fontSize: 11, fontStyle: 'italic', padding: 10, textAlign: 'center'}}>No bidding data</div>;
    
    const startIdx = SEATS.indexOf(dealer) !== -1 ? SEATS.indexOf(dealer) : 3; 
    const cells = Array(startIdx).fill({ call: '', alert: '' });
    
    bidding.forEach(b => {
        let call = b; let alert = '';
        if (b.includes('!')) { const parts = b.split('!'); call = parts[0]; alert = parts[1]; }
        cells.push({ call, alert });
    });

    const rows = [];
    for (let i = 0; i < cells.length; i += 4) { rows.push(cells.slice(i, i + 4)); }

    const formatBid = (call) => {
        if (!call) return null;
        const c = call.toUpperCase();
        if (c === 'P' || c === 'PASS') return <span style={{color: '#9cedc', fontWeight: 'bold'}}>Pass</span>;
        if (c === 'X' || c === 'D') return <span style={{color: '#ed7666', fontWeight: 'bold'}}>x</span>;
        if (c === 'XX' || c === 'R') return <span style={{color: '#6C5DD3', fontWeight: 'bold'}}>xx</span>;
        
        const match = c.match(/^([1-7])([CDHSN])/);
        if (match) {
            const level = match[1];
            const suit = match[2];
            const SYMBOLS = { 'S': '♠', 'H': '♥', 'D': '♦', 'C': '♣', 'N': 'NT' };
            const COLORS = { 'S': '#fff', 'H': '#FF3366', 'D': '#FF3366', 'C': '#fff', 'N': '#fff' };
            return (
                <span style={{fontWeight: 'bold', fontSize: 13}}>
                    <span style={{color: '#fff'}}>{level}</span>
                    <span style={{color: COLORS[suit]}}>{SYMBOLS[suit]}</span>
                </span>
            );
        }
        return <span style={{color: '#fff'}}>{c}</span>;
    };

    const HeaderCell = ({ seat, isVul }) => (
        <th style={{
            padding: '4px 0', width: '25%', 
            background: isVul ? 'rgb(169, 68, 68)' : '#F0F2F5', 
            color: isVul ? '#fff' : '#111',
            fontSize: 11, fontWeight: '900',
            borderRight: '1px solid rgba(0,0,0,0.1)',
            borderBottom: '1px solid rgba(0,0,0,0.2)'
        }}>
            {seat}
        </th>
    );

    return (
        <div 
            onClick={onClick}
            style={{ 
                background: '#1A1D26', borderRadius: 12, border: '2px solid rgba(255,255,255,0.1)', 
                width: 140, height: 140, cursor: onClick ? 'pointer' : 'default',
                transform: isExpanded ? 'scale(1)' : 'scale(0.85)', transformOrigin: 'center', transition: 'all 0.2s',
                overflowY: 'auto', msOverflowStyle: 'none', scrollbarWidth: 'none',
                boxShadow: 'inset 0 0 20px rgba(0,0,0,0.6)'
            }}
        >
            <table style={{width: '100%', textAlign: 'center', borderCollapse: 'collapse'}}>
                <thead style={{position: 'sticky', top: 0, zIndex: 2}}>
                    <tr>
                        <HeaderCell seat="W" isVul={vulData?.EW} />
                        <HeaderCell seat="N" isVul={vulData?.NS} />
                        <HeaderCell seat="E" isVul={vulData?.EW} />
                        <HeaderCell seat="S" isVul={vulData?.NS} />
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, rIdx) => (
                        <tr key={rIdx}>
                            {row.map((cell, cIdx) => (
                                <td key={cIdx} style={{padding: '6px 2px', borderBottom: '1px solid rgba(255,255,255,0.03)', borderRight: '1px solid rgba(255,255,255,0.02)'}} title={cell.alert}>
                                    <div style={{ background: cell.alert ? '#FFD700' : 'transparent', borderRadius: 3, cursor: cell.alert ? 'help' : 'default' }}>
                                        {formatBid(cell.call)}
                                    </div>
                                </td>
                            ))}
                            {[...Array(4 - row.length)].map((_, i) => <td key={'pad'+i} style={{borderBottom: '1px solid rgba(255,255,255,0.03)'}}></td>)}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

const TrickTracker = ({ trickHistory }) => {
    const [openIndex, setOpenIndex] = useState(null);

    useEffect(() => {
        const closeAll = () => setOpenIndex(null);
        window.addEventListener('click', closeAll);
        return () => window.removeEventListener('click', closeAll);
    }, []);

    return (
        <div style={{ display: 'flex', alignItems: 'flex-end', padding: '6px 10px', background: 'rgba(136, 136, 136, 0)', borderRadius: '6px', border: '0px solid rgba(255, 255, 255, 0.37)', width: 'max-content' }}>
            {[...Array(13)].map((_, i) => {
                const trick = trickHistory[i];
                if (!trick) return <div key={i} style={{ width: '12px', height: '30px', flexShrink: 0, borderBottom: '2px solid rgba(255,255,255,0.0)' }} />;

                const isNS = trick.winner === 'N' || trick.winner === 'S';
                const isOpen = openIndex === i;

                return (
                    <div 
                        key={i} 
                        onClick={(e) => { e.stopPropagation(); setOpenIndex(isOpen ? null : i); }} 
                        style={{ 
                            position: 'relative', 
                            width: '12px', // Consistent horizontal spacing
                            display: 'flex',
                            alignItems: 'flex-end',
                            zIndex: i, 
                            cursor: 'pointer' 
                        }}
                    >
                        <div style={{
                            width: isNS ? 20 : 30, 
                            height: isNS ? 30 : 20,
                            flexShrink: 0,
                            // Subtle gradient for more physical depth
                            background: isNS 
                                ? 'linear-gradient(135deg, #4A5568 0%, #363d4a 100%)' 
                                : 'linear-gradient(135deg, #2D3748 0%, #1a202c 100%)',
                            // Highlight border if active, otherwise standard
                            border: isOpen ? '1.5px solid #FFD700' : '0.5px solid rgb(173, 173, 173)', 
                            borderRadius: 2,
                            // Added a left-side shadow to distinguish from the card underneath
                            boxShadow: '-2px 0 4px rgba(0,0,0,0.4), 1px 0 3px rgba(0,0,0,0.5)',
                            transform: isOpen ? 'translateY(-2px)' : 'none',
                            transition: 'transform 0.1s'
                        }} />

                        {isOpen && (
                            <div style={{
                                position: 'absolute', 
                                bottom: '45px', 
                                // SMART ANCHORING: Shift right if it's an early trick, shift left if it's late
                                // left: i < 3 ? '0%' : (i > 9 ? 'auto' : '50%'),
                                // right: i > 9 ? '0%' : 'auto',
                                transform: i < 3 ? `translateX(-${19 + i * 12}px)` : 'translateX(-50%)',
                                background: '#0D0E15', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 8,
                                width: 110, height: 110, display: 'flex', justifyContent: 'center', alignItems: 'center',
                                zIndex: 9999999, // Extreme Z-Index to stay above everything
                                boxShadow: '0 10px 30px rgba(0,0,0,0.9)'
                            }}>
                                {trick.cards.map((play, ci) => {
                                    const pos = {
                                        N: { transform: 'translate(0px, -24px)' },
                                        S: { transform: 'translate(0px, 24px)' },
                                        W: { transform: 'translate(-26px, 4px)' },
                                        E: { transform: 'translate(26px, -4px)' }
                                    }[play.seat];

                                    return (
                                        <div key={ci} style={{
                                            position: 'absolute', 
                                            ...pos, 
                                            zIndex: ci,
                                            animation: `trickPop 0.25s ease-out ${ci * 0.1}s both`,
                                            // ADDED FOR DEPTH:
                                            borderRadius: 4, // Matches PlayingCard radius
                                            border: '0.5px solid rgb(178, 178, 178)', // Subtle definition border
                                            boxShadow: '2px 4px 8px rgba(0,0,0,0.5), 0 0 2px rgba(255,255,255,0.1)' // Outer drop shadow + faint top highlight
                                        }}>
                                            <PlayingCard card={play.card} />
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                );
            })}
            <style>{`
                @keyframes trickPop {
                    0% { opacity: 0; scale: 0.6; }
                    100% { opacity: 1; scale: 1; }
                }
            `}</style>
        </div>
    );
};

const DDTableDisplay = ({ tableData }) => {
    if (!tableData) return null;

    const strains = ['C', 'D', 'H', 'S', 'NT'];
    const players = ['N', 'S', 'E', 'W'];

    return (
        <div style={{ 
            background: 'rgba(65, 65, 65, 0.27)', 
            padding: '8px', 
            borderRadius: 8, 
            border: '1px solid rgba(255, 255, 255, 0.37)', 
            width: 'max-content',
            boxShadow: '0 4px 12px rgba(0,0,0,0.)' 
        }}>
            <table style={{ borderCollapse: 'collapse', fontSize: 11, textAlign: 'center' }}>
                <thead style={{background: 'rgba(65, 65, 65, 0.27)'}}>
                    <tr style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.35)' }}>
                        <th style={{ padding: '2px 6px' }}></th>
                        {strains.map(s => (
                            <th key={s} style={{ padding: '2px 6px', color: (s === 'H' || s === 'D') ? '#FF3366' : '#fff' }}>
                                {SUIT_SYMBOLS[s] || 'NT'}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {players.map(p => (
                        <tr key={p} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                            <td style={{ padding: '2px 6px', fontWeight: 'bold', fontSize: 11, color: '#fff', borderRight: '1px solid rgba(255,255,255,0.1)' }}>{p}</td>
                            {strains.map(s => {
                                const tricks = tableData[`${p}_${s}`];
                                return (
                                    <td key={`${p}_${s}`} style={{ padding: '2px 6px', color: '#fff', fontSize: 11 }}>
                                        {tricks !== undefined ? tricks : '-'}
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

const RoomAnalyzer = ({ 
    details, roomName, contractStr, rawScore, isActive,
    boardNum, players, aliases, colors, 
    displayMode, ddTableData, showDdTable
}) => {
    
    const vulData = getVulnerability(boardNum);
    
    // --- 1. STATE & REFS ---
    // Generate a unique cache key based on the board, room, and physical cards dealt!
    const stateKey = `ha_${boardNum}_${roomName}_${details?.hands?.substring(0, 15) || 'default'}`;

    const [step, setStep] = usePersistentState(`${stateKey}_step`, 0);
    const [customPlaySequence, setCustomPlaySequence] = usePersistentState(`${stateKey}_customPlay`, []);
    const [isEditingPlay, setIsEditingPlay] = usePersistentState(`${stateKey}_isEditing`, false);
    const [deviationStep, setDeviationStep] = usePersistentState(`${stateKey}_deviation`, null);
    
    // Hidden Hands State
    const [hiddenRegions, setHiddenRegions] = usePersistentState(`${stateKey}_hiddenRegions`, { N: false, S: false, E: false, W: false });
    const [flippedCards, setFlippedCards] = usePersistentState(`${stateKey}_flippedCards`, {});

    const [auctionExpanded, setAuctionExpanded] = useState(false);
    const [rawDdResults, setRawDdResults] = useState({});
    const [ddActive, setDdActive] = useState(false);

    const showFullAuction = true;
    const hideAuctionInPlay = true;
    const displayModeOverride = 'visual';

// When details.play changes (new data loaded), reset the custom sequence

    // const [ddTable, setDdTable] = useState(null);
    // const [showDdTable, setShowDdTable] = useState(false);
    const containerRef = useRef(null);
    const workerRef = useRef(null);

    const retroWorkerRef = useRef(null);
    const retroQueueRef = useRef([]); // Holds cards waiting to be evaluated
    const retroActiveRef = useRef(null);
    const clickTimeoutRef = useRef(null);

    // --- 2. DATA PARSING ---
    const initialHandsData = useMemo(() => reconstructHands(details?.hands), [details?.hands]);
    const cData = useMemo(() => parseContractStr(contractStr), [contractStr]);
    
    const totalBids = details?.bidding ? details.bidding.length : 0;
    const playSequence = details?.play || [];

    const totalPlays = customPlaySequence.length;
    
    // FIX: Ignore the original "claim" flag if we are exploring a hypothetical timeline
    const hasEndScreen = isEditingPlay ? (totalPlays === 52) : (details?.claim || totalPlays === 52);
    
    // const totalPlays = playSequence.length;

    // const hasEndScreen = details?.claim || totalPlays === 52;
    
    const currentBids = showFullAuction ? (details?.bidding || []) : (details?.bidding || []).slice(0, Math.min(step, totalBids));
    const isPlayPhase = showFullAuction ? true : step >= totalBids;
    
    // Capped at totalPlays so the math doesn't break when we advance to the extra summary step
    const playStep = showFullAuction 
        ? Math.min(step, totalPlays) 
        : Math.min(Math.max(0, step - totalBids), totalPlays);

    const [parScore, setParScore] = useState(null);
    
    // const currentBids = showFullAuction ? (details?.bidding || []) : (details?.bidding || []).slice(0, Math.min(step, totalBids));
    // const isPlayPhase = showFullAuction ? true : step >= totalBids;
    // const playStep = showFullAuction ? step : Math.max(0, step - totalBids);

    const WIN_COLOR = '#6cbd8c'; // Softer green
    const LOSE_COLOR = '#ed7666'; // Softer red
    const TIE_COLOR = '#6B7280';  // Grey for zero
    const handleBadgeClick = (seat) => {
        setHiddenRegions(prev => ({ ...prev, [seat]: !prev[seat] }));
    };

    const handleCardClick = (card, seat) => {
        if (!hiddenRegions[seat]) {
            // 1. Hand is visible: Instant play, absolutely no delay!
            handleCardPlay(card, seat);
            return;
        }

        // 2. Hand is hidden: Determine if it's a peek (single) or play (double)
        if (clickTimeoutRef.current) {
            // Second click detected! Cancel the flip and play the card instead.
            clearTimeout(clickTimeoutRef.current);
            clickTimeoutRef.current = null;
            handleCardPlay(card, seat);
        } else {
            // First click detected! Start the 250ms countdown.
            clickTimeoutRef.current = setTimeout(() => {
                // If the timer finishes without a second click, it's just a peek. Flip it!
                setFlippedCards(prev => ({ ...prev, [card.id]: !prev[card.id] }));
                clickTimeoutRef.current = null;
            }, 150); 
        }
    };

    const handleCardPlay = (card, seat) => {
        // 1. Only allow the player whose turn it is to play
        if (seat !== tableState.nextToPlay || !isPlayPhase) return;

        // 2. Enforce Bridge Rules (Must follow suit if possible)
        const leadSuit = (tableState.currentTrick.length > 0 && tableState.currentTrick.length < 4) ? tableState.currentTrick[0].card.suit : null;
        const unplayedCards = tableState.hands[seat].filter(c => !c.isPlayed);
        const hasLeadSuit = leadSuit && unplayedCards.some(c => c.suit === leadSuit);
        
        if (hasLeadSuit && card.suit !== leadSuit) {
            return; // Illegal play, ignore click
        }

        // 3. Fork the Timeline
        if (!isEditingPlay) {
            setIsEditingPlay(true);
            setDeviationStep(step); // Save the exact point in time we deviated
        }
        
        // Truncate the sequence to the current step, then append the clicked card
        const newSequence = [...customPlaySequence.slice(0, playStep), card.id];
        setCustomPlaySequence(newSequence);
        
        // 4. Wipe the DD Cache to prevent Option A "stale ghost data" bugs!
        if (mappedDdScores[card.id] !== undefined) {
            ddCacheRef.current[card.id] = mappedDdScores[card.id];
        }
        setRawDdResults({});
        
        // Advance the step automatically (safely accounting for auction offset)
        setStep(showFullAuction ? playStep + 1 : totalBids + playStep + 1);
    };

    const evaluateTrick = (trickCards, trumpSuit) => {
        if (trickCards.length === 0) return null;
        let winningCard = trickCards[0];

        for (let i = 1; i < trickCards.length; i++) {
            const c = trickCards[i];
            const wSuit = winningCard.card.suit;
            const cSuit = c.card.suit;
            const cRank = getRankValue(c.card.rank);
            const wRank = getRankValue(winningCard.card.rank);

            // Trump beats non-trump. Otherwise, highest rank of the leading suit wins.
            if (cSuit === trumpSuit && wSuit !== trumpSuit) {
                winningCard = c; 
            } else if (cSuit === wSuit && cRank > wRank) {
                winningCard = c; 
            }
        }
        return winningCard.seat;
    };

    const tableState = useMemo(() => {
        // Deep clone
        let hands = { 
            S: initialHandsData.S.map(c => ({...c, isPlayed: false})), 
            W: initialHandsData.W.map(c => ({...c, isPlayed: false})), 
            N: initialHandsData.N.map(c => ({...c, isPlayed: false})), 
            E: initialHandsData.E.map(c => ({...c, isPlayed: false})) 
        };

        let trickHistory = [];
        
        let currentTrick = []; 
        let lastTrick = []; // Holds the 4 cards of the *previous* trick temporarily
        let tricksWon = { NS: 0, EW: 0 };
        
        const declarerIdx = SEATS.indexOf(cData.declarer);
        let currentLeader = declarerIdx !== -1 ? SEATS[(declarerIdx + 1) % 4] : 'W'; 
        let trickWinner = null;

        for (let i = 0; i < playStep && i < customPlaySequence.length; i++) {
            // const playedCardId = playSequence[i].toUpperCase().replace(/\s/g, '').replace('10', 'T');
            const playedCardId = customPlaySequence[i].toUpperCase().replace(/\s/g, '').replace('10', 'T');
            
            // If the current trick is full (4 cards), and a 5th is played, 
            // WE NOW clear it to start the next trick.
            if (currentTrick.length === 4) {
                trickHistory.push({ winner: evaluateTrick(currentTrick, cData.trump), cards: [...currentTrick] });
                lastTrick = [...currentTrick];
                trickWinner = evaluateTrick(currentTrick, cData.trump);
                if (trickWinner === 'N' || trickWinner === 'S') tricksWon.NS += 1; else tricksWon.EW += 1;
                currentLeader = trickWinner; 
                currentTrick = []; // Clear for the new card
            }

            SEATS.forEach(seat => {
                const cardObj = hands[seat].find(c => c.id === playedCardId);
                if (cardObj && !cardObj.isPlayed) {
                    cardObj.isPlayed = true;
                    currentTrick.push({ seat: seat, card: cardObj });
                }
            });
        }
        
        // If we finished a loop and exactly 4 cards are sitting there, 
        // figure out who won it for UI display purposes, but DON'T clear it yet.
        if (currentTrick.length === 4) {
            trickWinner = evaluateTrick(currentTrick, cData.trump);
        }

        const nextToPlay = currentTrick.length === 0 || currentTrick.length === 4 ? (trickWinner || currentLeader) : SEATS[(SEATS.indexOf(currentLeader) + currentTrick.length) % 4];
        
        return { hands, currentTrick, lastTrick, tricksWon, currentLeader, nextToPlay, trickWinner, trickHistory };
    }, [initialHandsData, playSequence, playStep, cData]);

    const getHistoricalState = (targetStep) => {
        let hHands = { 
            S: initialHandsData.S.map(c => ({...c, isPlayed: false})), 
            W: initialHandsData.W.map(c => ({...c, isPlayed: false})), 
            N: initialHandsData.N.map(c => ({...c, isPlayed: false})), 
            E: initialHandsData.E.map(c => ({...c, isPlayed: false})) 
        };
        let hTrick = [];
        let hWon = { NS: 0, EW: 0 };
        const declarerIdx = SEATS.indexOf(cData.declarer);
        let hLeader = declarerIdx !== -1 ? SEATS[(declarerIdx + 1) % 4] : 'W'; 
        let hWinner = null;

        for (let i = 0; i < targetStep; i++) {
            const pid = customPlaySequence[i].toUpperCase().replace(/\s/g, '').replace('10', 'T');
            if (hTrick.length === 4) {
                hWinner = evaluateTrick(hTrick, cData.trump);
                if (hWinner === 'N' || hWinner === 'S') hWon.NS += 1; else hWon.EW += 1;
                hLeader = hWinner; 
                hTrick = []; 
            }
            SEATS.forEach(seat => {
                const c = hHands[seat].find(x => x.id === pid);
                if (c && !c.isPlayed) { c.isPlayed = true; hTrick.push({ seat, card: c }); }
            });
        }
        if (hTrick.length === 4) hWinner = evaluateTrick(hTrick, cData.trump);
        
        const hNextToPlay = hTrick.length === 0 || hTrick.length === 4 ? (hWinner || hLeader) : SEATS[(SEATS.indexOf(hLeader) + hTrick.length) % 4];
        
        // Build the PBN just like the main thread does
        const trickIds = hTrick.length === 4 ? [] : hTrick.map(t => t.card.id);
        const formatHand = (arr) => {
            const valid = arr.filter(c => !c.isPlayed || trickIds.includes(c.id));
            const getS = (s) => valid.filter(c => c.suit === s).map(c => c.rank === '10' ? 'T' : c.rank).join('');
            return `${getS('S')}.${getS('H')}.${getS('D')}.${getS('C')}`;
        };
        const pbnStr = `N:${formatHand(hHands.N)} ${formatHand(hHands.E)} ${formatHand(hHands.S)} ${formatHand(hHands.W)}`;
        
        return { pbnStr, hTrick, hWon, hNextToPlay, hLeader, hWinner };
    };

    // --- 3. EFFECTS ---

    useEffect(() => {
        setCustomPlaySequence(details?.play || []);
        setIsEditingPlay(false);
    }, [details?.play]);
    
    // Click-outside listener for Bidding Box
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) setAuctionExpanded(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // BULLETPROOF WORKER INITIALIZATION
    const processNextRetro = () => {
        if (!retroWorkerRef.current || retroActiveRef.current || retroQueueRef.current.length === 0) return;
        
        // Pop the next card requested from the queue
        const nextJob = retroQueueRef.current.shift();
        retroActiveRef.current = nextJob;

        const trumpInt = getTrumpInt(cData.trump);
        const isTrickFull = nextJob.hTrick.length === 4;
        const firstInt = isTrickFull ? getSeatInt(nextJob.hWinner) : getSeatInt(nextJob.hLeader);
        const trickStr = isTrickFull ? "" : nextJob.hTrick.map(play => play.card.id).join(',');

        retroWorkerRef.current.postMessage({
            trump: trumpInt, first: firstInt, pbn: nextJob.pbnStr, currentTrick: trickStr
        });
    };

    useEffect(() => {
        if (isActive && !workerRef.current) {
            // Main UI Worker
            workerRef.current = new Worker(`/ddsWorker.js?v=${DDS_WASM_VERSION}`);
            workerRef.current.onmessage = (e) => {
                if (e.data.type === 'RESULT') setRawDdResults(e.data.data);
            };

            // Background Historical Worker
            retroWorkerRef.current = new Worker(`/ddsWorker.js?v=${DDS_WASM_VERSION}`);
            retroWorkerRef.current.onmessage = (e) => {
                if (e.data.type === 'RESULT' && retroActiveRef.current) {
                    const job = retroActiveRef.current;
                    const rawRetro = e.data.data;
                    
                    // Do the exact same math as mappedDdScores, but using historical variables
                    const target = Number(cData.level) + 6;
                    const declarerSide = (cData.declarer === 'N' || cData.declarer === 'S') ? 'NS' : 'EW';
                    const targetSide = (job.hNextToPlay === 'N' || job.hNextToPlay === 'S') ? 'NS' : 'EW';
                    
                    const completedTricks = Math.floor(job.step / 4);
                    const remainingTricks = 13 - completedTricks;
                    const isTrickFull = job.hTrick.length === 4;
                    
                    const effWon = {
                        NS: job.hWon.NS + (isTrickFull && (job.hWinner === 'N' || job.hWinner === 'S') ? 1 : 0),
                        EW: job.hWon.EW + (isTrickFull && (job.hWinner === 'E' || job.hWinner === 'W') ? 1 : 0)
                    };

                    const absoluteTricks = rawRetro[job.cardId];
                    if (absoluteTricks !== undefined) {
                        const tricksNum = Number(absoluteTricks);
                        let finalTotal = targetSide === declarerSide 
                            ? effWon[declarerSide] + tricksNum 
                            : effWon[declarerSide] + (remainingTricks - tricksNum);
                            
                        // Silently inject into the UI Cache!
                        ddCacheRef.current[job.cardId] = finalTotal - target;
                        // Force a tiny re-render so the pill appears immediately
                        setRawDdResults(prev => ({...prev})); 
                    }

                    // Clear active job and trigger the next one
                    retroActiveRef.current = null;
                    processNextRetro();
                }
            };
        }
        return () => {
            if (!isActive) {
                if (workerRef.current) { workerRef.current.terminate(); workerRef.current = null; }
                if (retroWorkerRef.current) { retroWorkerRef.current.terminate(); retroWorkerRef.current = null; }
            }
        };
    }, [isActive, cData]);

    // TRIGGER WORKER ON TRICK START
    useEffect(() => {
        // 1. SAFE WIPE: Instantly clears old scores so React never calculates ghost numbers
        setRawDdResults({});

        // 2. DIAGRAM UNLOCK: Removed !isPlayPhase so DD runs during the auction phase
        if (!isActive || !ddActive || cData.isPass) {
            return;
        }
        
        const pbnStr = convertToPBN(tableState.hands, tableState.currentTrick);
        if (pbnStr) {
            const trumpInt = getTrumpInt(cData.trump);

            const isTrickFull = tableState.currentTrick.length === 4;
            const firstInt = isTrickFull ? getSeatInt(tableState.trickWinner) : getSeatInt(tableState.currentLeader);
            const trickStr = isTrickFull ? "" : tableState.currentTrick.map(play => play.card.id).join(',');

            // console.log(`📡 SENDING TO WASM -> Trump: ${trumpInt} | First: ${firstInt} | Trick: ${trickStr} | PBN:`, pbnStr);

            workerRef.current?.postMessage({
                trump: trumpInt,
                first: firstInt,
                pbn: pbnStr,
                currentTrick: trickStr
            });
        }
    }, [isActive, ddActive, isPlayPhase, tableState.hands, tableState.currentTrick, tableState.currentLeader, tableState.trickWinner, cData]);

    useEffect(() => {
        // If DD is off, or no cards are on the table, do nothing
        if (!ddActive || !isActive || tableState.currentTrick.length === 0) return;
        
        // Grab the most recently played card currently sitting on the table
        const lastPlayIdx = tableState.currentTrick.length - 1;
        const lastPlay = tableState.currentTrick[lastPlayIdx];
        
        // If it's missing from the cache, we need to secretly fetch it!
        if (ddCacheRef.current[lastPlay.card.id] === undefined) {
            
            // The exact moment BEFORE this card was played
            const targetStep = playStep - 1; 
            
            // Prevent spamming the queue if we are already fetching it
            const inQueue = retroQueueRef.current.some(job => job.cardId === lastPlay.card.id);
            const isProcessing = retroActiveRef.current?.cardId === lastPlay.card.id;
            
            if (!inQueue && !isProcessing) {
                // Spin up the time machine
                const historicalState = getHistoricalState(targetStep);
                
                // Add to the background worker queue
                retroQueueRef.current.push({
                    cardId: lastPlay.card.id,
                    step: targetStep,
                    ...historicalState
                });
                
                // Fire the engine!
                processNextRetro();
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ddActive, isActive, tableState.currentTrick, playStep]);

    // --- 4. TRANSLATE DD RESULTS ---
    const mappedDdScores = useMemo(() => {
        if (!rawDdResults || Object.keys(rawDdResults).length === 0 || !cData || cData.isPass) return {};
        const scores = {};
        
        const target = Number(cData.level) + 6;
        const declarerSide = (cData.declarer === 'N' || cData.declarer === 'S') ? 'NS' : 'EW';
        
        // 1. PERSPECTIVE: Calculate ONLY for the player whose turn it currently is
        const targetSeat = tableState.nextToPlay;
        const targetSide = (targetSeat === 'N' || targetSeat === 'S') ? 'NS' : 'EW';

        // 2. IDENTIFY LEGAL CARDS: Must follow suit if possible
        const unplayedTargetCards = tableState.hands[targetSeat].filter(c => !c.isPlayed);
        const leadSuit = (tableState.currentTrick.length > 0 && tableState.currentTrick.length < 4) ? tableState.currentTrick[0].card.suit : null;
        const hasLeadSuit = leadSuit && unplayedTargetCards.some(c => c.suit === leadSuit);
        
        // Array of IDs for cards that are legally allowed to be played right now
        const legalCardIds = hasLeadSuit 
            ? unplayedTargetCards.filter(c => c.suit === leadSuit).map(c => c.id) 
            : unplayedTargetCards.map(c => c.id);

        // 3. TOUCHING CARDS FIX: Group by absolute unplayed sequence
        const filledResults = { ...rawDdResults };
        
        // Get ALL unplayed cards across the entire table to accurately define "touching"
        const allUnplayed = [];
        SEATS.forEach(s => {
            tableState.hands[s].forEach(c => {
                if (!c.isPlayed) allUnplayed.push({ ...c, seat: s });
            });
        });

        SUITS.forEach(suit => {
            // Sort all remaining cards in this suit globally from highest to lowest
            const suitUnplayed = allUnplayed.filter(c => c.suit === suit).sort((a, b) => getRankValue(b.rank) - getRankValue(a.rank));
            
            // Forward pass: Copy scores down to touching cards
            let currentScore = null;
            for (let i = 0; i < suitUnplayed.length; i++) {
                const c = suitUnplayed[i];
                if (c.seat !== targetSeat) {
                    currentScore = null; // Sequence broken by another player's card!
                } else {
                    if (filledResults[c.id] !== undefined) currentScore = filledResults[c.id];
                    else if (currentScore !== null) filledResults[c.id] = currentScore;
                }
            }
            
            // Backward pass: Copy scores up (in case DDS returned the bottom of the sequence)
            currentScore = null;
            for (let i = suitUnplayed.length - 1; i >= 0; i--) {
                const c = suitUnplayed[i];
                if (c.seat !== targetSeat) {
                    currentScore = null; // Sequence broken by another player's card!
                } else {
                    if (rawDdResults[c.id] !== undefined) currentScore = rawDdResults[c.id];
                    else if (currentScore !== null && filledResults[c.id] === undefined) filledResults[c.id] = currentScore;
                }
            }
        });

        // 4. MATHEMATICAL TRANSLATION (Applying only to legal cards)
        const isTrickFull = tableState.currentTrick.length === 4;

        // Because we stripped full tricks from the PBN, the DD solver evaluates
        // based on the number of tricks completely finished.
        const completedTricks = Math.floor(playStep / 4);
        const remainingTricks = 13 - completedTricks;

        // If the trick is exactly 4 cards, tableState.tricksWon hasn't swept it into the 
        // official tally yet, but the DD solver assumes it's already in the bank. We must manually add it.
        const effectiveTricksWon = {
            NS: tableState.tricksWon.NS + (isTrickFull && (tableState.trickWinner === 'N' || tableState.trickWinner === 'S') ? 1 : 0),
            EW: tableState.tricksWon.EW + (isTrickFull && (tableState.trickWinner === 'E' || tableState.trickWinner === 'W') ? 1 : 0)
        };

        legalCardIds.forEach(cardId => {
            const absoluteTricks = filledResults[cardId];
            if (absoluteTricks !== undefined) {
                const tricksNum = Number(absoluteTricks);
                let declarerFinalTotal;

                if (targetSide === declarerSide) {
                    // Declarer is playing: Final total is past tricks + future tricks
                    declarerFinalTotal = effectiveTricksWon[declarerSide] + tricksNum;
                } else {
                    // Defender is playing: Declarer's final total is declarer's past tricks + declarer's future tricks
                    // (Defender's future tricks = tricksNum. Declarer's future tricks = remainingTricks - tricksNum)
                    const remainingForDeclarer = remainingTricks - tricksNum;
                    declarerFinalTotal = effectiveTricksWon[declarerSide] + remainingForDeclarer;
                }

                scores[cardId] = declarerFinalTotal - target;
            }
        });

        // console.log(`🔍 DD CHECK (Trick ${Math.floor(playStep / 4) + 1}) | Seat: ${targetSeat}`, {
        //     legalCardsAllowed: legalCardIds,
        //     rawAbsoluteTricksFromSolver: filledResults,
        //     finalBadgeScores: scores,
        //     targetNeeded: target,
        // });
        
        return scores;
    }, [rawDdResults, cData, tableState, playStep]);

    const ddCacheRef = useRef({});
    useEffect(() => {
        if (Object.keys(mappedDdScores).length > 0) {
            ddCacheRef.current = { ...ddCacheRef.current, ...mappedDdScores };
        }
    }, [mappedDdScores]);

    // --- 5. EARLY RETURNS ---
    if (!details || !details.hands) return <div style={{padding: 40, color: '#555', textAlign: 'center', fontSize: 12, border: '1px dashed rgba(255,255,255,0.1)', borderRadius: 8, flex: 1}}>No data available for {roomName} Room.</div>;

    const pNames = {
        N: aliases?.N || players[2] || 'North', 
        S: aliases?.S || players[0] || 'South', 
        E: aliases?.E || players[3] || 'East', 
        W: aliases?.W || players[1] || 'West' 
    };

    // const maxStep = showFullAuction ? totalPlays : totalBids + totalPlays;
    const maxStep = showFullAuction 
        ? totalPlays + (hasEndScreen ? 1 : 0) 
        : totalBids + totalPlays + (hasEndScreen ? 1 : 0);

    return (
        <div ref={containerRef} style={{ background: '#11131A', padding: '15px', borderRadius: 12, border: '1px solid rgba(255,255,255,0.05)', position: 'relative', flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', height: '100%' }}>
            
            <div style={{ flex: 1, position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                
                {/* Clean, fixed Top-Left Info Corner */}
                {/* <div style={{ position: 'absolute', top: 0, left: 0, zIndex: 10, width: 100 }}>
                    <div style={{ background: 'rgba(0,0,0,0.6)', borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)', overflow: 'hidden', textAlign: 'center' }}>
                        <div style={{ background: 'rgba(255,255,255,0.05)', color: '#A0AABF', fontSize: 9, fontWeight: 'bold', padding: '4px', textTransform: 'uppercase', letterSpacing: 1 }}>{vulData.label} Vul</div>
                        <div style={{ padding: '6px' }}>
                            {cData.isPass ? (
                                <div style={{ fontSize: 14, fontWeight: 'bold', color: '#fff' }}>Pass</div>
                            ) : (
                                <>
                                    <div style={{ fontSize: 15, fontWeight: 'bold', color: '#fff' }}>{cData.level}<span style={{color: SUIT_COLORS[cData.suit] || '#fff'}}>{SUIT_SYMBOLS[cData.suit] || cData.suit}</span>{cData.dbl}</div>
                                    <div style={{ fontSize: 11, color: '#A0AABF' }}>By {cData.declarer}</div>
                                </>
                            )}
                        </div>
                    </div>
                </div> */}

                <div style={{ color: '#E0E0E0', fontSize: 13, fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 15, display: 'flex', gap: 6, alignItems: 'center' }}>
                    <span style={{ color: '#fff' }}>
                        {roomName}:
                    </span> 
                    {/* {formatContract(contractStr)} */}
                </div>
                {/* <div style={{ color: '#E0E0E0', fontSize: 13, fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 2, marginBottom: 15 }}>{roomName} Room</div> */}

                {displayModeOverride === 'visual' ? (
                    <div className="ha-table-container">
                        <div className="ha-hand-north">
                            {/* Contract Box */}
                            <div style={{ position: 'absolute', top: 0, left: 0, zIndex: 10, width: 100, pointerEvents: 'auto' }}>
                                <div style={{ background: 'rgba(0,0,0,0.6)', borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)', overflow: 'hidden', textAlign: 'center' }}>
                                    <div style={{ background: 'rgba(255,255,255,0.05)', color: '#A0AABF', fontSize: 9, fontWeight: 'bold', padding: '4px', textTransform: 'uppercase', letterSpacing: 1 }}>{vulData.label} Vul</div>
                                    <div style={{ padding: '6px' }}>
                                        {cData.isPass ? (
                                            <div style={{ fontSize: 14, fontWeight: 'bold', color: '#fff' }}>Pass</div>
                                        ) : (
                                            <>
                                                <div style={{ fontSize: 15, fontWeight: 'bold', color: '#fff' }}>
                                                    {cData.level}
                                                    <span style={{color: (cData.suit === 'C' || cData.suit === 'S') ? '#fff' : SUIT_COLORS[cData.suit] || '#fff'}}>
                                                        {SUIT_SYMBOLS[cData.suit] || 'NT'}
                                                    </span>
                                                    {cData.declarer}{cData.dbl}
                                                    {cData.res && (
                                                        <span style={{ marginLeft: 4 }}>
                                                            {cData.res}{cData.tricks}
                                                        </span>
                                                    )}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {showDdTable && ddTableData && (
                                <div style={{ 
                                    position: 'absolute', top: 0, right: 0, zIndex: 10, 
                                    transform: 'scale(0.8)', transformOrigin: 'top right',
                                    pointerEvents: 'auto' // Re-enable clicks for DD Table
                                }}>
                                    <DDTableDisplay tableData={ddTableData} />
                                </div>
                            )}
                            
                            {/* North Hand Component with Roster Name */}
                            <div style={{ pointerEvents: 'auto' }}>
                                <PlayerHand 
                                    seat="N" pName={pNames.N} realName={aliases?.N} 
                                    isVul={vulData.NS} teamColor={colors.NS} cards={tableState.hands.N} 
                                    ddScores={mappedDdScores} trump={cData.trump} 
                                    onCardClick={handleCardClick}
                                    onBadgeClick={handleBadgeClick}
                                    isHiddenRegion={hiddenRegions.N}
                                    flippedCards={flippedCards}
                                />
                            </div>
                        </div>
                        <div className="ha-hand-west">
                            <PlayerHand 
                                seat="W" pName={pNames.W} realName={aliases?.W} 
                                isVul={vulData.EW} teamColor={colors.EW} cards={tableState.hands.W} 
                                ddScores={mappedDdScores} trump={cData.trump} 
                                onCardClick={handleCardClick}
                                onBadgeClick={handleBadgeClick}
                                isHiddenRegion={hiddenRegions.W} // <--- Looks at .W
                                flippedCards={flippedCards}
                            />
                        </div>
                        
                        
                        <div className="ha-trick-area">
                            {/* State 1: Auction */}
                            {(!isPlayPhase || playStep === 0) && !(step === maxStep && hasEndScreen) ? (
                                <BiddingBox bidding={currentBids} dealer={initialHandsData.dealer} isExpanded={true} vulData={vulData} />
                            ) : 
                            /* State 2: End of Game / Claim */
                            isPlayPhase && ((step === maxStep && hasEndScreen && !isEditingPlay) || (isEditingPlay && playStep === 52)) ? (
                                
                                <div style={{
                                    background: 'rgba(13, 14, 21, 0.95)', 
                                    border: '1px solid rgba(255,215,0,0.8)', // Thinner border
                                    padding: '12px 24px', // Tighter padding
                                    borderRadius: 12, 
                                    textAlign: 'center', 
                                    boxShadow: '0 0 20px rgba(0,0,0,0.8), 0 0 10px rgba(255,215,0,0.1)', // Subtler backdrop
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 6 // Tighter gap
                                }}>
                                {/* <div style={{
                                    background: 'rgba(13, 14, 21, 0.95)', 
                                    border: '2px solid rgba(255, 217, 0, 0.43)', 
                                    padding: '20px 40px', 
                                    borderRadius: 16, 
                                    textAlign: 'center', 
                                    boxShadow: '0 0 40px rgba(0,0,0,0.8), 0 0 20px rgba(255,215,0,0.1)',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 10
                                }}> */}
                                    <div style={{color: 'rgba(255, 217, 0, 0.66)', fontSize: 22, fontWeight: '900', textTransform: 'uppercase', letterSpacing: 2}}>
                                        {isEditingPlay ? '(Result)' :(details.claim ? 'Claimed' : 'Result')}
                                    </div>
                                    
                                    {(() => {
                                        let displayRes = cData.res;
                                        let displayTricks = cData.tricks;
                                        let displayHomeScore = roomName === 'Away' ? -rawScore : rawScore;

                                        // Calculate the dynamic result if we are at the end of a hypothetical timeline
                                        if (isEditingPlay && playStep === 52 && !cData.isPass) {
                                            const declarerSide = (cData.declarer === 'N' || cData.declarer === 'S') ? 'NS' : 'EW';
                                            
                                            // Add the final trick sitting on the table to the permanent tally
                                            const finalTricksNS = tableState.tricksWon.NS + (tableState.currentTrick.length === 4 && (tableState.trickWinner === 'N' || tableState.trickWinner === 'S') ? 1 : 0);
                                            const finalTricksEW = tableState.tricksWon.EW + (tableState.currentTrick.length === 4 && (tableState.trickWinner === 'E' || tableState.trickWinner === 'W') ? 1 : 0);
                                            
                                            const decTricks = declarerSide === 'NS' ? finalTricksNS : finalTricksEW;
                                            const target = parseInt(cData.level) + 6;
                                            const diff = decTricks - target;

                                            if (diff === 0) { displayRes = '='; displayTricks = ''; }
                                            else if (diff > 0) { displayRes = '+'; displayTricks = diff.toString(); }
                                            else { displayRes = ''; displayTricks = diff.toString(); } // diff is already negative, so it prints "-1"

                                            displayHomeScore = null; // Hide the raw numerical score during hypotheticals to avoid confusion
                                        }

                                        return (
                                            <>
                                                <div style={{ fontSize: 18, fontWeight: 'bold', color: '#fff' }}>
                                                    {cData.level}
                                                    <span style={{color: (cData.suit === 'C' || cData.suit === 'S') ? '#fff' : SUIT_COLORS[cData.suit] || '#fff'}}>
                                                        {SUIT_SYMBOLS[cData.suit] || 'NT'}
                                                    </span>
                                                    {cData.dbl}{cData.declarer} <span style={{color: '#FFD700'}}>{displayRes}{displayTricks}</span>
                                                </div>
                                                
                                                {displayHomeScore !== null && (
                                                    <>
                                                        <div style={{ height: 1, background: 'rgba(255,255,255,0.1)', width: '60%', margin: '5px auto' }} />
                                                        <div style={{color: '#A0AABF', fontSize: 16, fontWeight: 'bold'}}>
                                                            {roomName === 'Away' ? "EW: " : "NS: "} 
                                                            <span style={{color: displayHomeScore > 0 ? WIN_COLOR : (displayHomeScore < 0 ? LOSE_COLOR : '#fff')}}>
                                                                {displayHomeScore > 0 ? `+${displayHomeScore}` : displayHomeScore}
                                                            </span>
                                                        </div>
                                                    </>
                                                )}
                                            </>
                                        );
                                    })()}
                                </div>
                            ) : 
                            /* State 3: Active Trick Area */
                            (
                                <div style={{ width: 140, height: 140, background: '#1A4A28', borderRadius: '16px', border: '2px solid rgba(255,255,255,0.1)', position: 'relative', boxShadow: 'inset 0 0 30px rgba(0,0,0,0.8)' }}>
                                    <div style={{ position: 'absolute', top: 4, left: 4, color: '#A0AABF', fontSize: 10, fontWeight: 'bold' }}>NS: <span style={{color: '#fff'}}>{tableState.tricksWon.NS}</span></div>
                                    <div style={{ position: 'absolute', bottom: 4, right: 4, color: '#A0AABF', fontSize: 10, fontWeight: 'bold' }}>EW: <span style={{color: '#fff'}}>{tableState.tricksWon.EW}</span></div>
                                    
                                    {tableState.currentTrick.map((play, i) => {
                                        const isWinner = tableState.currentTrick.length === 4 && play.seat === tableState.trickWinner;
                                        const isMostRecent = i === tableState.currentTrick.length - 1;
                                        const recentScore = (isMostRecent && ddActive) ? ddCacheRef.current[play.card.id] : null;
                                        const anyScore = (ddActive) ? ddCacheRef.current[play.card.id] : null;
                                        const pos = {
                                            N: { top: 10, left: '50%', transform: 'translateX(-50%)' },
                                            S: { bottom: 10, left: '50%', transform: 'translateX(-50%)' },
                                            W: { left: 10, top: '50%', transform: 'translateY(-50%)' },
                                            E: { right: 10, top: '50%', transform: 'translateY(-50%)' }
                                        }[play.seat];
                                        
                                        return (
                                            <div key={i} style={{ 
                                                position: 'absolute', ...pos, zIndex: isWinner ? 10 : i, 
                                                // Subtler glow for the winning card
                                                boxShadow: isWinner ? '0 0 8px rgba(255, 215, 0, 0.4)' : 'none', 
                                            }}>
                                                {/* use recentScore for just latest card */}
                                                <PlayingCard card={play.card} ddScore={recentScore} /> 
                                            </div>
                                        );
                                        // return (
                                        //     <div key={i} style={{ position: 'absolute', ...pos, zIndex: isWinner ? 10 : i, boxShadow: isWinner ? '0 0 15px rgba(255, 215, 0, 0.8)' : 'none', transition: '0.3s' }}>
                                        //         <PlayingCard card={play.card} />
                                        //     </div>
                                        // );
                                    })}
                                </div>
                            )}
                        </div>

                        <div className="ha-hand-east">
                            <PlayerHand 
                                seat="E" pName={pNames.E} isVul={vulData.EW} realName={aliases?.E} teamColor={colors.EW} cards={tableState.hands.E} 
                                ddScores={mappedDdScores} trump={cData.trump} 
                                onCardClick={handleCardClick}
                                onBadgeClick={handleBadgeClick}
                                isHiddenRegion={hiddenRegions.E} // <--- Looks at .E
                                flippedCards={flippedCards}
                            />
                        </div>
                        <div className="ha-hand-south">
                            {/* TRICK TRACKER: Pinned Left */}
                            <div style={{ position: 'absolute', left: 0, bottom: 0, pointerEvents: 'auto' }}>
                                <TrickTracker trickHistory={tableState.trickHistory} />
                            </div>

                            {/* PLAYER HAND: Centered */}
                            <div style={{ pointerEvents: 'auto' }}>
                                <PlayerHand 
                                    seat="S" pName={pNames.S} realName={aliases?.S} 
                                    isVul={vulData.NS} teamColor={colors.NS} cards={tableState.hands.S} 
                                    ddScores={mappedDdScores} trump={cData.trump} 
                                    onCardClick={handleCardClick}
                                    onBadgeClick={handleBadgeClick}
                                    isHiddenRegion={hiddenRegions.S}
                                    flippedCards={flippedCards}
                                />
                            </div>
                        </div>
                    </div>
                ) : (
                    <HandDiagram hands={tableState.hands} ddScores={mappedDdScores} />
                )}
            </div>
            <div style={{ marginTop: 20, paddingTop: 15, borderTop: '1px solid rgba(255,255,255,0.05)', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                
                <button onClick={() => setStep(0)} style={{background: 'rgba(255,255,255,0.05)', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: 4, cursor: 'pointer', fontSize: 11}}>|&lt;</button>
                <button onClick={() => setStep(Math.max(0, step - 1))} style={{background: 'rgba(255,255,255,0.1)', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: 4, cursor: 'pointer', fontSize: 11, fontWeight: 'bold'}}>&lt; Prev</button>
                
                {/* <span style={{color: '#FFD700', fontWeight: 'bold', fontSize: 11, width: 80, textAlign: 'center'}}>
                    {!isPlayPhase ? `Bid ${step + 1}` : `Trick ${Math.floor(playStep/4) + 1}`}
                </span> */}
                <span style={{color: '#FFD700', fontWeight: 'bold', fontSize: 11, width: 80, textAlign: 'center'}}>
                    {(!isPlayPhase || playStep === 0) ? (showFullAuction ? 'Auction' : `Bid ${step + 1}`) : `Trick ${Math.floor((playStep - 1) / 4) + 1}`}
                </span>
                
                <button onClick={() => setStep(Math.min(maxStep, step + 1))} style={{background: '#6C5DD3', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: 4, cursor: 'pointer', fontSize: 11, fontWeight: 'bold'}}>Next &gt;</button>
                <button onClick={() => setStep(maxStep)} style={{background: 'rgba(255,255,255,0.05)', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: 4, cursor: 'pointer', fontSize: 11}}>&gt;|</button>
                
                {/* Vertical Divider */}
                <div style={{ width: 1, height: 20, background: 'rgba(255,255,255,0.1)', margin: '0 5px' }} />

                {/* Local DD Toggle */}
                <button 
                    onClick={() => setDdActive(!ddActive)} 
                    style={{
                        display: 'flex', alignItems: 'center', gap: 6,
                        background: ddActive ? 'rgba(74, 222, 128, 0.15)' : 'rgba(0,0,0,0.3)', 
                        color: ddActive ? '#4ADE80' : '#A0AABF', 
                        border: `1px solid ${ddActive ? 'rgba(74, 222, 128, 0.4)' : 'rgba(255,255,255,0.1)'}`, 
                        padding: '6px 12px', 
                        borderRadius: 4, 
                        cursor: 'pointer', 
                        fontSize: 11, 
                        fontWeight: 'bold',
                        transition: 'all 0.2s'
                    }}
                >
                    📊 {ddActive ? 'DD Active' : 'Show DD'}
                </button>

                {isEditingPlay && (
                    <button 
                        onClick={() => {
                            setCustomPlaySequence(details?.play || []);
                            setIsEditingPlay(false);
                            setStep(deviationStep !== null ? deviationStep : 0); // Snap exactly back to the branch point
                            setDeviationStep(null);
                        }} 
                        style={{background: '#E53E3E', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: 4, cursor: 'pointer', fontSize: 11, fontWeight: 'bold'}}
                    >
                        Reset Custom Play
                    </button>
                )}
            </div>
        </div>
    );
};


// --- 3. MAIN COMPONENT ---
const HandAnalyzer = ({ board, players = [], aliases = {}, colors = {}, isActive, t1Data, t2Data, t1Color, t2Color, swapRooms }) => {
    const { isMobile } = useWindowSize();

        
    // PERSISTENT SETTINGS
    const [roomLayout, setRoomLayout] = usePersistentState('ha_roomLayout', 'both'); 
    const [displayMode, setDisplayMode] = usePersistentState('ha_displayMode', 'visual'); 
    const [hidePlayed, setHidePlayed] = usePersistentState('ha_hidePlayed', true);
    const [showFullAuction, setShowFullAuction] = usePersistentState('ha_showFullAuction', false);
    const [hideAuctionInPlay, setHideAuctionInPlay] = usePersistentState('ha_hideAuctionInPlay', false);
    const [ddActive, setDdActive] = usePersistentState('bridge_dd_active', false);
    const [showDdTable, setShowDdTable] = useState(false);
    const [ddTableData, setDdTableData] = useState(null);
    const [menuOpen, setMenuOpen] = useState(false);
    const [parScore, setParScore] = useState(null);
    const workerRef = useRef(null);

    // const vulData = getVulnerability(board?.boardNum);

    useEffect(() => {
        if (isActive && !workerRef.current) {
            workerRef.current = new Worker(`/ddsWorker.js?v=${DDS_WASM_VERSION}`);
            workerRef.current.onmessage = (e) => {
                if (e.data.type === 'TABLE_RESULT') {
                    setDdTableData(e.data.data);
                }
                if (e.data.parScore || e.data.par) {
                    setParScore(e.data.parScore || e.data.par);
                }
            };
        }
        return () => {
            if (!isActive && workerRef.current) {
                workerRef.current.terminate();
                workerRef.current = null;
            }
        };
    }, [isActive]);

    const fetchTable = () => {
        setShowDdTable(!showDdTable);
        if (!ddTableData && workerRef.current) {
            // Reconstruct a clean PBN from the board details. 
            // (Use the Open room hands as the base)
            const baseHands = reconstructHands(board?.details?.o?.hands);
            const formatOpen = (h) => {
                const getS = (suit) => h.filter(c=>c.suit===suit).map(c=>c.rank==='10'?'T':c.rank).join('');
                return `${getS('S')}.${getS('H')}.${getS('D')}.${getS('C')}`;
            };
            const pbn = `N:${formatOpen(baseHands.N)} ${formatOpen(baseHands.E)} ${formatOpen(baseHands.S)} ${formatOpen(baseHands.W)}`;
            workerRef.current.postMessage({ action: 'CALC_DD_TABLE', pbn });
        }
    };

    const openAliases = t1Data ? { N: t1Data?.north, S: t1Data?.south, E: t2Data?.east, W: t2Data?.west } : aliases;
    const closedAliases = t2Data ? { N: t2Data?.north, S: t2Data?.south, E: t1Data?.east, W: t1Data?.west } : aliases;
    
    const openColors = t1Color ? { NS: t1Color, EW: t2Color } : colors;
    const closedColors = t2Color ? { NS: t2Color, EW: t1Color } : colors;

    const homeDetails = swapRooms ? board?.details?.c : board?.details?.o;
    const awayDetails = swapRooms ? board?.details?.o : board?.details?.c;
    const homeContract = swapRooms ? board?.room2 : board?.room1;
    const awayContract = swapRooms ? board?.room1 : board?.room2;
    const homeRaw = swapRooms ? board?.raw2 : board?.raw1;
    const awayRaw = swapRooms ? board?.raw1 : board?.raw2;
    const homeAliases = swapRooms ? closedAliases : openAliases;
    const awayAliases = swapRooms ? openAliases : closedAliases;
    const homeColors = swapRooms ? closedColors : openColors;
    const awayColors = swapRooms ? openColors : closedColors;

    const isSingleView = roomLayout === 'open' || roomLayout === 'closed';

    return (
        <div style={{ padding: '5px 0' }}>
            <style>{`

            .ha-table-container {
                display: grid;
                /* Flexible outer columns to prevent spillover, fixed center for trick area */
                grid-template-columns: minmax(130px, 1fr) 160px minmax(130px, 1fr);
                grid-template-rows: auto 160px auto; /* Let rows size naturally based on content */
                gap: 8px;
                width: 100%; /* Changed from max-content to 100% to respect parent bounds */
                max-width: 600px; /* Constrain max width per room */
                margin: 0 auto;
                background: transparent; /* Removed the inner box background since RoomAnalyzer provides one */
                padding: 0; /* Removed padding to maximize space */
            }
            /* North spans 3 columns so contract box can be pinned to far left of this row */
            // .ha-hand-north { 
            //     grid-column: 1 / span 3; 
            //     grid-row: 1; 
            //     display: flex; 
            //     justify-content: center; 
            //     align-items: flex-start; 
            //     position: relative; 
            // }
            // .ha-hand-south { grid-column: 1 / span 3; grid-row: 3; display: flex; justify-content: center; align-items: flex-end; position: relative; z-index: 1000; }
            .ha-hand-north { 
                grid-column: 1 / span 3; 
                grid-row: 1; 
                display: flex; 
                justify-content: center; 
                align-items: flex-start; 
                position: relative; 
                z-index: 1000;
                pointer-events: none; 
            }
            /* South spans 3 columns, allow clicks to pass through its empty space */
            .ha-hand-south { 
                grid-column: 1 / span 3; 
                grid-row: 3; 
                display: flex; 
                justify-content: center; 
                align-items: flex-end; 
                position: relative; 
                z-index: 1000; 
                pointer-events: none; 
            }
            .ha-hand-west { grid-column: 1; grid-row: 2; display: flex; align-items: center; justify-content: flex-start; }
            .ha-hand-east { grid-column: 3; grid-row: 2; display: flex; align-items: center; justify-content: flex-end; }
            // .ha-hand-west { grid-column: 1; grid-row: 2; display: flex; align-items: center; justify-content: flex-start; }
            // .ha-hand-east { grid-column: 3; grid-row: 2; display: flex; align-items: center; justify-content: flex-end; }
            .ha-trick-area { grid-column: 2; grid-row: 2; display: flex; justify-content: center; align-items: center; position: relative; }
            
            
            .ha-settings-menu {
                position: absolute; right: 0; top: 100%; margin-top: 5px;
                background: rgba(20, 24, 34, 0.95); border: 1px solid rgba(255,255,255,0.1);
                border-radius: 8px; padding: 10px; box-shadow: 0 10px 25px rgba(0,0,0,0.5);
                z-index: 1000; width: max-content; display: flex; flex-direction: column; gap: 8px;
            }
        `}</style>
            <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginBottom: 5, background: 'rgba(0,0,0,0.2)', padding: '10px 15px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.05)' }}>
    
                {/* 1. Side-by-Side Room Buttons */}
                <div style={{ display: 'flex', background: 'rgba(0,0,0,0.4)', borderRadius: 6, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.1)' }}>
                    {/* CHANGED to home/away */}
                    {['both', 'home', 'away'].map(mode => (
                        <button 
                            key={mode}
                            onClick={() => setRoomLayout(mode)}
                            style={{ 
                                background: roomLayout === mode ? '#6C5DD3' : 'transparent', 
                                color: roomLayout === mode ? '#fff' : '#A0AABF', 
                                border: 'none', padding: '6px 14px', fontSize: 11, fontWeight: 'bold', cursor: 'pointer', textTransform: 'capitalize',
                                transition: 'background 0.2s'
                            }}
                        >
                            {mode}
                        </button>
                    ))}
                </div>

                {/* 3. DD Table Toggle Button */}
                <button onClick={fetchTable} style={{ background: showDdTable ? 'rgba(255,255,255,0.1)' : 'transparent', color: showDdTable ? '#fff' : '#A0AABF', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 6, padding: '6px 12px', fontSize: 11, fontWeight: 'bold', cursor: 'pointer', transition: 'all 0.2s' }}>
                    DD Table
                </button>

                {/* 2. Par Score (Always visible) */}
                {/* {ddTableData ? (
                    // <div style={{fontSize: 12, display: 'flex', alignItems: 'center', gap: 8}}>
                    //     <span style={{color: '#fff', fontWeight: 'bold'}}>Par:</span>
                    //     {getParScore(ddTableData, getVulnerability(board?.boardNum))}
                    // </div>
                    <div style={{fontSize: 12, color: '#fff', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 6}}>
                        {parScore ? (
                            `Par: ${parScore}` 
                        ) : (
                            // Fallback to max makeable contracts if exact Par isn't provided by the worker
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span style={{color: '#A0AABF'}}>Makeable:</span> 
                                {getOptimalContracts(ddTableData)}
                            </div>
                        )}
                    </div>
                ) : (
                    <div style={{fontSize: 12, color: 'rgba(255,255,255,0.4)', fontWeight: 'bold'}}>Par: --</div>
                )} */}
                
            </div>

            {/* Render the Table centrally if toggled */}
            {/* {showDdTable && ddTableData && (
                <div style={{marginBottom: 15, display: 'flex', justifyContent: 'center'}}>
                    <DDTableDisplay tableData={ddTableData} />
                </div>
            )} */}

            <div style={{ display: 'flex', gap: 15, width: '100%', flexDirection: (isMobile || isSingleView) ? 'column' : 'row' }}>
                {(() => {
                    return (
                        <>
                            <div style={{ display: 'flex', gap: 10, width: '100%', flexDirection: (isMobile || isSingleView) ? 'column' : 'row' }}>
                                <div style={{ display: (roomLayout === 'both' || roomLayout === 'home') ? 'flex' : 'none', flex: 1, minWidth: 0 }}>
                                    <RoomAnalyzer 
                                        details={homeDetails} roomName="Home" isActive={isActive} contractStr={homeContract} rawScore={homeRaw} 
                                        boardNum={board?.boardNum} players={players} aliases={homeAliases} colors={homeColors}
                                        displayMode={displayMode} hidePlayed={hidePlayed} showFullAuction={showFullAuction} hideAuctionInPlay={hideAuctionInPlay} ddActive={ddActive}
                                        ddTableData={ddTableData}
                                        showDdTable={showDdTable}
                                    />
                                </div>
                                <div style={{ display: (roomLayout === 'both' || roomLayout === 'away') ? 'flex' : 'none', flex: 1, minWidth: 0 }}>
                                    <RoomAnalyzer 
                                        details={awayDetails} roomName="Away" isActive={isActive} contractStr={awayContract} rawScore={awayRaw}
                                        boardNum={board?.boardNum} players={players} aliases={awayAliases} colors={awayColors}
                                        displayMode={displayMode} hidePlayed={hidePlayed} showFullAuction={showFullAuction} hideAuctionInPlay={hideAuctionInPlay} ddActive={ddActive} ddTableData={ddTableData}
                                        showDdTable={showDdTable}
                                    />
                                </div>
                            </div>
                        </>
                    );
                })()}
            </div>
        </div>
    );
};

export default HandAnalyzer;